/**
 * arbi convo list                    — list conversations
 * arbi convo switch <id>             — resume an older conversation
 * arbi convo threads [id]            — show threads (picker if no ID)
 * arbi convo delete [id]             — delete (picker if no ID)
 * arbi convo share [id]              — share (picker if no ID)
 * arbi convo title [id] [title]      — update title (picker + prompt)
 * arbi convo message <message-id>    — get message details
 * arbi convo trace <message-id>      — full decrypted execution trace
 * arbi convo delete-message <msg-id> — delete one message + descendants
 *
 * Note the command name is `convo`, not `conversations` — nobody types
 * the long form. There is no alias.
 */

import { Command } from 'commander'
import { readFileSync } from 'node:fs'
import { basename, resolve } from 'node:path'
import { conversations as convOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveAuth,
  resolveWorkspace,
  printTable,
  printJson,
  dryRun,
} from '../helpers.js'
import { updateChatSession } from '../config.js'
import { promptSelect, promptInput } from '../prompts.js'
import { success, error, ref } from '../output.js'

/** Fetch workspace conversations and return as picker choices */
async function pickConversation(
  arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi'],
  workspaceId: string,
  message = 'Select conversation'
) {
  const data = await convOps.listConversations(arbi)
  if (data.length === 0) {
    process.stderr.write('No conversations found.\n')
    process.exit(0)
  }
  return promptSelect(
    message,
    data.map((c) => ({
      name: `${(c.title as string) ?? 'Untitled'} (${c.message_count} msgs)`,
      value: c.external_id as string,
      description: c.external_id as string,
    }))
  )
}

export function registerConversationsCommand(program: Command): void {
  const conv = program
    .command('convo')
    .description('Conversations: list, switch, threads, delete, share, title, message, trace')

  conv
    .command('list')
    .description('List conversations in the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--json', 'Output as JSON')
    .action((opts: { workspace?: string; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await convOps.listConversations(arbi)

        if (opts.json) {
          printJson(data)
          return
        }

        if (data.length === 0) {
          process.stderr.write('No conversations found.\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 24, value: (r) => r.external_id as string },
            { header: 'TITLE', width: 36, value: (r) => (r.title as string) ?? 'Untitled' },
            { header: 'MSGS', width: 6, value: (r) => String(r.message_count) },
            { header: 'SHARED', width: 8, value: (r) => (r.is_shared ? 'yes' : 'no') },
          ],
          data
        )
      })()
    )

  conv
    .command('import <path>')
    .description(
      'Import a conversation export (ChatGPT, OpenClaw, or Claude Code) into the active workspace'
    )
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-s, --source <source>', 'Export format: auto, chatgpt, openclaw, claude_code', 'auto')
    .option('--json', 'Output as JSON')
    .action((path: string, opts: { workspace?: string; source?: string; json?: boolean }) =>
      runAction(async () => {
        const { loginResult, config } = await resolveWorkspace(opts.workspace)
        const filePath = resolve(path)
        let bytes: Buffer
        try {
          bytes = readFileSync(filePath)
        } catch {
          error(`Cannot read file: ${filePath}`)
          process.exit(3)
        }
        const blob = new Blob([new Uint8Array(bytes)])
        const data = await convOps.importConversations(
          {
            baseUrl: config.baseUrl,
            accessToken: loginResult.accessToken,
          } as Parameters<typeof convOps.importConversations>[0],
          blob,
          basename(filePath),
          opts.source ?? 'auto'
        )

        if (opts.json) {
          printJson(data)
          return
        }

        success(
          `Imported ${data.imported_count} conversation(s) from ${data.source} ` +
            `(${data.imported_message_count} messages, ${data.skipped_node_count} skipped)`
        )
        for (const c of data.conversations) {
          process.stdout.write(`  ${ref(c.conversation_ext_id)}  ${c.title}\n`)
        }
      })()
    )

  conv
    .command('threads [conversation-id]')
    .description('Show threads (interactive picker if no ID given)')
    .action((conversationId?: string) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace()
        if (!conversationId) conversationId = await pickConversation(arbi, workspaceId)
        const data = await convOps.getConversationThreads(arbi, conversationId)
        console.log(JSON.stringify(data, null, 2))
      })()
    )

  // ── switch ───────────────────────────────────────────────────────────────
  // CLI equivalent of clicking a different conversation in the frontend
  // sidebar. Looks up the latest message in the target conversation and
  // points the shell's chat-session pointer at it, so the next `arbi ask`
  // continues the chosen conversation. Without this an agent could only
  // continue the most recent conversation (auto-tracked) or pass --continue
  // with a raw message-id it had to find some other way.
  conv
    .command('switch <conversation-id>')
    .description(
      'Continue a previous conversation — next `arbi ask` resumes from its latest message'
    )
    .action((conversationId: string) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace()
        const data = (await convOps.getConversationThreads(arbi, conversationId)) as {
          conversation_ext_id?: string
          threads?: Array<{
            leaf_message_ext_id?: string
            history?: Array<Record<string, unknown>>
          }>
        }
        // Threads shape from the backend:
        //   { conversation_ext_id, threads: [{leaf_message_ext_id, history: [msg, ...]}, ...] }
        // The "latest" point to resume is the longest thread's leaf_message_ext_id
        // (or, if missing, the last assistant message in its history). We don't
        // need to walk all threads — the backend already orders them so the
        // primary thread is index 0.
        const threads = data.threads ?? []
        if (threads.length === 0) {
          process.stderr.write(`Conversation ${conversationId} has no threads.\n`)
          process.exit(3)
        }
        const primary = threads[0]
        const leafId = primary.leaf_message_ext_id
        const lastAssistant = [...(primary.history ?? [])]
          .reverse()
          .find((m) => (m.role as string) === 'assistant') as Record<string, unknown> | undefined
        const resumeId = leafId ?? (lastAssistant?.external_id as string | undefined)
        if (!resumeId) {
          process.stderr.write(
            `No resumable message in conversation ${conversationId}. ` +
              `(Empty thread or never reached an assistant turn.)\n`
          )
          process.exit(3)
        }
        updateChatSession({
          conversationExtId: conversationId,
          lastMessageExtId: resumeId,
          workspaceId,
        })
        success(`Switched to conversation ${conversationId} (resume from ${resumeId}).`)
      })()
    )

  conv
    .command('delete [conversation-id]')
    .description('Delete a conversation (interactive picker if no ID given)')
    .option('--dry-run', 'Preview which conversation would be deleted (no SDK call)')
    .action((conversationId: string | undefined, opts: { dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace()
        if (!conversationId)
          conversationId = await pickConversation(
            arbi,
            workspaceId,
            'Select conversation to delete'
          )
        if (opts?.dryRun) {
          dryRun('delete conversation', conversationId)
          return
        }
        const data = await convOps.deleteConversation(arbi, conversationId)
        success(data?.detail ?? `Deleted conversation ${conversationId}`)
      })()
    )

  conv
    .command('share [conversation-id]')
    .description('Share a conversation (interactive picker if no ID given)')
    .action((conversationId?: string) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace()
        if (!conversationId)
          conversationId = await pickConversation(arbi, workspaceId, 'Select conversation to share')
        const data = await convOps.shareConversation(arbi, conversationId)
        success(data?.detail ?? `Shared conversation ${conversationId}`)
      })()
    )

  conv
    .command('title [conversation-id] [title]')
    .description('Update conversation title (interactive if no args)')
    .action((conversationId?: string, title?: string) =>
      runAction(async () => {
        const { arbi, workspaceId } = await resolveWorkspace()
        if (!conversationId) conversationId = await pickConversation(arbi, workspaceId)
        if (!title) title = await promptInput('New title')
        const data = await convOps.updateConversationTitle(arbi, conversationId, title)
        success(data?.detail ?? `Updated title to: ${title}`)
      })()
    )

  conv
    .command('message <message-id>')
    .description('Get message details (full decrypted JSON, useful for debugging)')
    .action((messageId: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await convOps.getMessage(arbi, messageId)
        printJson(data)
      })()
    )

  // ── trace ────────────────────────────────────────────────────────────────
  // Decrypted execution trace for a single agent response — which tools were
  // called, which docs were cited, intermediate reasoning. Same endpoint as
  // `conversations message` underneath, but presented as the post-mortem
  // primitive an agent reaches for after a step fails. JSON-only by design:
  // human rendering would lose half the structure.
  conv
    .command('trace <message-id>')
    .description('Decrypted execution trace for one agent response (alias for `message`)')
    .action((messageId: string) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        const data = await convOps.getMessage(arbi, messageId)
        printJson(data)
      })()
    )

  conv
    .command('delete-message <message-id>')
    .description('Delete a message and its descendants')
    .action((messageId: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await convOps.deleteMessage(arbi, messageId)
        success(data?.detail ?? `Deleted message ${messageId}`)
      })()
    )

  // Default action: list. Forward raw argv tail rather than pre-declaring
  // --json/-w on the parent — see tags.ts for the commander-binding bug.
  conv
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = conv.args ?? []
      await conv.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
